/**
 * usage-core.ts — [REQ-AUG-30 M.6 / USAGE-1] the PURE half of the usage tracker: the buffer and
 * the counting functions, with ZERO imports.
 *
 * 🔴 WHY THE SPLIT EXISTS (found by the scripts-tsc gate, not by taste): `notification-routes.ts`
 * gained the tap hook, and `reqaug26-band5-probe.ts` imports that file — so a single-module
 * tracker (whose flush imports `services/api`) dragged api.ts into the SCRIPTS compile graph and
 * its two pre-existing `import.meta.env`-under-CJS errors with it (140 → 142). Counting must be
 * importable by anything — a route map, a share helper, a probe-imported util — without pulling
 * transport behind it. The flush machinery, the api import and the lifecycle hooks live in
 * `./usage`; THIS module must never import anything (probe-pinned).
 *
 * The silence property is inherited trivially here: no imports, no network, no storage, no DOM —
 * a counter increment cannot fail loudly because it cannot fail.
 */
export type UsagePortal = 'student' | 'teacher' | 'admin' | 'superadmin' | 'employee';
export type UsageMetric = 'open' | 'action' | 'refusal' | 'notif_tap';

export interface UsageEntryOut {
  featureKey: string;
  metric: UsageMetric;
  count: number;
  deviceClass: string;
}

let portal: UsagePortal | null = null;
let currentScreenKey: string | null = null;
const buffer = new Map<string, number>();

const deviceClass: 'mobile' | 'desktop' =
  typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop';

/** '|' — a printable separator no registry key can contain ([a-z0-9_.] only). */
function bufferKey(featureKey: string, metric: UsageMetric): string {
  return `${featureKey}|${metric}`;
}

/** The transport half calls this to set the shell's portal literal at init. */
export function setUsagePortal(p: UsagePortal | null): void {
  portal = p;
}

export function getUsagePortal(): UsagePortal | null {
  return portal;
}

export function hasBufferedUsage(): boolean {
  return buffer.size > 0;
}

export function clearUsageBuffer(): void {
  buffer.clear();
}

/** Drain up to one batch (the route caps at 50 entries). Clears the buffer either way. */
export function drainToEntries(): UsageEntryOut[] {
  const entries = [...buffer.entries()].slice(0, 50).map(([k, count]) => {
    const [featureKey, metric] = k.split('|');
    return { featureKey, metric: metric as UsageMetric, count, deviceClass };
  });
  buffer.clear();
  return entries;
}

/**
 * Count one touch. Safe to call before init (dropped — a shared component rendered outside a
 * tracked shell, e.g. the owner portal, records nothing by design).
 */
export function trackUsage(featureKey: string, metric: UsageMetric = 'action'): void {
  if (!portal) return;
  const k = bufferKey(featureKey, metric);
  buffer.set(k, (buffer.get(k) ?? 0) + 1);
}

/** The route-watcher's screen-open hook; also remembers the key for refusal attribution. */
export function trackScreenOpen(featureKey: string): void {
  currentScreenKey = featureKey;
  trackUsage(featureKey, 'open');
}

/**
 * [USAGE-1] The refusal metric — fired by the api interceptor's two refusal exits (registered as
 * a callback by ./usage's init). Attributed to the SCREEN the user was on — "where do users hit
 * walls", not which endpoint.
 */
export function trackRefusal(): void {
  if (!portal || !currentScreenKey) return;
  trackUsage(currentScreenKey, 'refusal');
}

/** Probe/HMR hygiene only — not called by product code. */
export function __resetUsageCoreForTest(): void {
  portal = null;
  currentScreenKey = null;
  buffer.clear();
}
